import axios from 'axios'
import { toast } from '@/hooks/use-toast'

export type ErrorToastContext = {
  operation?: 'query' | 'mutation'
  entity?: string
  action?: string
}

function getMessageFromAxios(data: any): string | undefined {
  if (!data) return undefined
  if (typeof data === 'string') return data
  // FastAPI validation errors often provide detail as an array of objects
  const detail = Array.isArray(data?.detail)
    ? data.detail.map((d: any) => (d?.msg || d?.type || JSON.stringify(d))).join(', ')
    : data?.detail
  const errorsJoined = Array.isArray(data?.errors)
    ? data.errors.map((e: any) => (typeof e === 'string' ? e : JSON.stringify(e))).join(', ')
    : undefined
  const msg = detail || data?.message || data?.error || errorsJoined
  return typeof msg === 'string' ? msg : (msg ? JSON.stringify(msg) : undefined)
}

export function toastQueryError(error: unknown, ctx?: ErrorToastContext) {
  // Ignore cancellations (common in React Query during unmount/refetch)
  const code = (error as any)?.code
  if (code === 'ERR_CANCELED') return

  // AxiosError handling
  if (axios.isAxiosError(error)) {
    const status = error.response?.status
    // Avoid spamming users on unauthorized flows; interceptor handles logout
    if (status === 401) return
    // 409 Conflict is often expected (e.g., linked items) - let component handle it
    if (status === 409) return

    const serverMessage = getMessageFromAxios(error.response?.data)
    const desc = serverMessage || error.message || 'Une erreur est survenue.'

    toast({
      title: 'Erreur',
      description: ctx?.entity || ctx?.action ? `${desc}` : desc,
      variant: 'destructive',
    })
    return
  }

  // Generic Error
  if (error instanceof Error) {
    toast({ title: 'Erreur', description: error.message, variant: 'destructive' })
    return
  }

  // Fallback unknown
  try {
    const text = JSON.stringify(error)
    toast({ title: 'Erreur', description: text, variant: 'destructive' })
  } catch {
    toast({ title: 'Erreur', description: 'Une erreur inattendue est survenue.', variant: 'destructive' })
  }
}
